import { useEffect } from "react";
import { useT } from "@/i18n";

/**
 * The browser tab's title — `<subject> · Sales Board`.
 *
 * One seam, called by each top-level view with its own name and by each detail modal with the
 * record's. Two behaviours make it worth being a hook rather than a line in each component:
 *
 * - **It restores what it replaced.** A detail modal is rendered *over* a surface that is still
 *   mounted, so both call this; the modal wins while it is open and the cleanup puts the
 *   surface's title back when it closes. Effects clean up innermost-first, which is exactly the
 *   ordering that needs. Without it, closing an opportunity would leave the deal's name in the
 *   tab over a board.
 * - **It follows the language.** The subject comes from the catalogue at the call site, and the
 *   join comes from `title.document`, so switching to Czech retitles the tab as well as the
 *   page.
 *
 * `undefined` means "not yet known" — a record still loading — and leaves the title alone
 * rather than flashing a placeholder into the tab and the browser's history.
 */
export function useDocumentTitle(subject: string | undefined): void {
  const t = useT();

  useEffect(() => {
    if (subject === undefined) return;

    const previous = document.title;
    document.title = t("title.document", subject);
    return () => {
      document.title = previous;
    };
  }, [subject, t]);
}
